import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CheckCircle2,
  ClipboardList,
  Layers3,
  PackageCheck,
  ShieldCheck,
  Sprout,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import type {
  NurseryStockSummary,
  NurseryVariety,
  SeedlingDistributionPlan,
  SeedlingDistributionTarget,
  VarietyDetailStatus,
} from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ErrorState, LoadingState } from "@/components/shared/StatePanels";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const number = new Intl.NumberFormat("en-UG");

type IssuePanelState = {
  target: SeedlingDistributionTarget;
  actualReceived: string;
  distributionDate: string;
  sourceQuantities: Record<string, string>;
  notes: string;
  recordVarieties: boolean;
  varietyStatus: VarietyDetailStatus;
  selectedVarieties: string[];
  varietyQuantities: Record<string, string>;
};

type VarietyPanelState = {
  target: SeedlingDistributionTarget;
  status: VarietyDetailStatus;
  selectedVarieties: string[];
  quantities: Record<string, string>;
};

const today = () => new Date().toISOString().slice(0, 10);

export default function SeedlingDistributionPlanDetailPage() {
  const { planId = "" } = useParams();
  const [plan, setPlan] = useState<SeedlingDistributionPlan>();
  const [stock, setStock] = useState<NurseryStockSummary[]>();
  const [varieties, setVarieties] = useState<NurseryVariety[]>();
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);
  const [issuePanel, setIssuePanel] = useState<IssuePanelState>();
  const [varietyPanel, setVarietyPanel] = useState<VarietyPanelState>();

  const load = useCallback(async () => {
    try {
      const [planRecord, stockRows, varietyRows] = await Promise.all([
        services.nursery.getDistributionPlan(planId),
        services.nursery.listStock(),
        services.nursery.listVarieties(),
      ]);
      if (!planRecord) throw new Error("Seedling Distribution Plan was not found.");
      setPlan(planRecord);
      setStock(stockRows);
      setVarieties(varietyRows.filter((item) => item.active));
      setError("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Seedling Distribution Plan could not be loaded.");
    }
  }, [planId]);

  useEffect(() => { void load(); }, [load]);

  const availableStock = useMemo(
    () => (stock || []).filter((row) => row.readyQuantity > 0 && row.lifecycleStatus !== "ARCHIVED"),
    [stock],
  );

  if (error) return <ErrorState message={error} />;
  if (!plan || !stock || !varieties) return <LoadingState label="Loading Seedling Distribution Plan" />;

  const sanction = async () => {
    setWorking(true);
    try {
      const updated = await services.nursery.sanctionDistributionPlan(plan.id, "Seedling distribution programme approved");
      setPlan(updated);
      toast.success("Seedling Distribution Plan sanctioned", {
        description: "Targets can now be issued from distribution-ready Nursery lots.",
      });
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Plan could not be sanctioned.");
    } finally {
      setWorking(false);
    }
  };

  const openIssue = (target: SeedlingDistributionTarget) => {
    setVarietyPanel(undefined);
    setIssuePanel({
      target,
      actualReceived: String(target.plannedQuantity),
      distributionDate: today(),
      sourceQuantities: {},
      notes: "",
      recordVarieties: false,
      varietyStatus: "PARTIAL",
      selectedVarieties: [],
      varietyQuantities: {},
    });
  };

  const targetStock = issuePanel
    ? availableStock.filter((row) => row.seedlingItemId === issuePanel.target.seedlingItemId)
    : [];

  const sourceTotal = issuePanel
    ? Object.values(issuePanel.sourceQuantities).reduce((sum, value) => sum + (Number(value) || 0), 0)
    : 0;

  const knownVarietyTotal = issuePanel
    ? issuePanel.selectedVarieties.reduce((sum, varietyId) => sum + (Number(issuePanel.varietyQuantities[varietyId]) || 0), 0)
    : 0;

  const issue = async () => {
    if (!issuePanel) return;
    const actual = Number(issuePanel.actualReceived);
    if (!Number.isFinite(actual) || actual <= 0) {
      toast.error("Enter the actual number of seedlings received.");
      return;
    }
    if (!issuePanel.distributionDate) {
      toast.error("Select the distribution date.");
      return;
    }
    const sources = targetStock
      .map((row) => ({ nurseryLotId: row.lotId, quantity: Number(issuePanel.sourceQuantities[row.lotId]) || 0 }))
      .filter((row) => row.quantity > 0);
    if (!sources.length) {
      toast.error("Select at least one Nursery lot as the seedling source.");
      return;
    }
    if (sourceTotal !== actual) {
      toast.error(`Nursery source quantities must total exactly ${number.format(actual)} seedlings.`);
      return;
    }

    let detailStatus: VarietyDetailStatus = "NOT_RECORDED";
    let varietyRows: Array<{ varietyId: string; quantity?: number }> = [];
    if (issuePanel.recordVarieties) {
      if (!issuePanel.selectedVarieties.length) {
        toast.error("Select at least one variety, or switch variety recording off for now.");
        return;
      }
      detailStatus = issuePanel.varietyStatus;
      varietyRows = issuePanel.selectedVarieties.map((varietyId) => {
        const raw = issuePanel.varietyQuantities[varietyId];
        return { varietyId, quantity: raw ? Number(raw) : undefined };
      });
      if (knownVarietyTotal > actual) {
        toast.error("Known variety quantities cannot exceed the actual seedlings received.");
        return;
      }
      if (detailStatus === "COMPLETE") {
        const hasUnknown = varietyRows.some((row) => row.quantity === undefined);
        if (hasUnknown || knownVarietyTotal !== actual) {
          toast.error("Complete variety detail must account for every seedling received.");
          return;
        }
      }
    }

    setWorking(true);
    try {
      await services.nursery.issueDistributionTarget(plan.id, issuePanel.target.id, {
        actualReceivedQuantity: actual,
        distributionDate: issuePanel.distributionDate,
        sources,
        varietyDetailStatus: detailStatus,
        varieties: varietyRows,
        notes: issuePanel.notes.trim() || undefined,
      });
      setIssuePanel(undefined);
      await load();
      toast.success("Seedlings issued to Plot", {
        description: `${number.format(actual)} seedlings recorded as received. Variety detail: ${detailStatus.replace("_", " ").toLowerCase()}.`,
      });
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Seedlings could not be issued.");
    } finally {
      setWorking(false);
    }
  };

  const openVariety = async (target: SeedlingDistributionTarget) => {
    setIssuePanel(undefined);
    setWorking(true);
    try {
      const existing = await services.nursery.listDistributionVarieties(target.id);
      setVarietyPanel({
        target,
        status: target.varietyDetailStatus,
        selectedVarieties: existing.map((row) => row.varietyId),
        quantities: Object.fromEntries(
          existing
            .filter((row) => row.quantity !== undefined)
            .map((row) => [row.varietyId, String(row.quantity)]),
        ),
      });
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Variety detail could not be loaded.");
    } finally {
      setWorking(false);
    }
  };

  const saveVarieties = async () => {
    if (!varietyPanel) return;
    const basis = varietyPanel.target.actualReceivedQuantity || varietyPanel.target.plannedQuantity;
    const rows = varietyPanel.selectedVarieties.map((varietyId) => {
      const raw = varietyPanel.quantities[varietyId];
      return { varietyId, quantity: raw ? Number(raw) : undefined };
    });
    const known = rows.reduce((sum, row) => sum + (row.quantity || 0), 0);
    if (known > basis) {
      toast.error("Known variety quantities cannot exceed the seedling total.");
      return;
    }
    if (varietyPanel.status === "COMPLETE") {
      if (rows.some((row) => row.quantity === undefined) || known !== basis) {
        toast.error(`Complete variety detail must reconcile exactly to ${number.format(basis)} seedlings.`);
        return;
      }
    }

    setWorking(true);
    try {
      await services.nursery.updateDistributionVarieties(varietyPanel.target.id, {
        varietyDetailStatus: varietyPanel.selectedVarieties.length ? varietyPanel.status : "NOT_RECORDED",
        varieties: rows,
      });
      setVarietyPanel(undefined);
      await load();
      toast.success("Variety detail updated");
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Variety detail could not be updated.");
    } finally {
      setWorking(false);
    }
  };

  const updateTargetStatus = async (
    target: SeedlingDistributionTarget,
    status: "PLANNED" | "READY" | "MISSED" | "CANCELLED",
  ) => {
    setWorking(true);
    try {
      await services.nursery.updateDistributionTarget(plan.id, target.id, status);
      await load();
      toast.success(`Target marked ${status.toLowerCase()}`);
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Target status could not be updated.");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="seedling-distribution-plan-detail">
      <PageHeader
        eyebrow="Distributions · Seedlings"
        title={plan.title}
        description={`${plan.planCode} · ${plan.plannedStartDate}${plan.plannedEndDate !== plan.plannedStartDate ? ` → ${plan.plannedEndDate}` : ""}`}
        backTo="/distributions"
        action={
          plan.status === "DRAFT" ? (
            <Button onClick={sanction} disabled={working}>
              <ShieldCheck className="mr-2 size-4" />Sanction Plan
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <StatusBadge status={plan.status} testId="seedling-plan-detail-status" />
        <span className="text-sm text-muted-foreground">
          Responsible staff: <span className="font-semibold text-foreground">{plan.responsibleEmployeeName || "Not resolved"}</span>
        </span>
        {plan.sanctionedAt && (
          <span className="text-sm text-muted-foreground">
            Sanctioned {new Date(plan.sanctionedAt).toLocaleString()}
          </span>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card><CardContent className="p-5"><ClipboardList className="size-5 text-primary" /><p className="mt-3 text-3xl font-semibold">{plan.targetCount}</p><p className="text-sm text-muted-foreground">Plot allocations</p></CardContent></Card>
        <Card><CardContent className="p-5"><Sprout className="size-5 text-primary" /><p className="mt-3 text-3xl font-semibold">{number.format(plan.totalPlannedSeedlings)}</p><p className="text-sm text-muted-foreground">Seedlings planned</p></CardContent></Card>
        <Card><CardContent className="p-5"><PackageCheck className="size-5 text-primary" /><p className="mt-3 text-3xl font-semibold">{number.format(plan.totalReceivedSeedlings)}</p><p className="text-sm text-muted-foreground">Actually received</p></CardContent></Card>
        <Card><CardContent className="p-5"><CheckCircle2 className="size-5 text-primary" /><p className="mt-3 text-3xl font-semibold">{plan.issuedCount} / {plan.targetCount}</p><p className="text-sm text-muted-foreground">Targets issued</p></CardContent></Card>
      </div>

      {issuePanel && (
        <Card className="border-primary/25" data-testid="seedling-issue-panel">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Sprout className="size-4 text-primary" />
              Issue seedlings · {issuePanel.target.farmerName} · {issuePanel.target.plotName}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Planned quantity</Label>
                <Input value={String(issuePanel.target.plannedQuantity)} disabled />
              </div>
              <div className="space-y-2">
                <Label>Actual seedlings received</Label>
                <Input
                  type="number"
                  min="1"
                  value={issuePanel.actualReceived}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setIssuePanel((current) => current ? ({ ...current, actualReceived: e.target.value }) : current)}
                />
              </div>
              <div className="space-y-2">
                <Label>Distribution date</Label>
                <Input
                  type="date"
                  value={issuePanel.distributionDate}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setIssuePanel((current) => current ? ({ ...current, distributionDate: e.target.value }) : current)}
                />
              </div>
            </div>

            <div>
              <h3 className="font-semibold">Nursery source lot(s)</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Choose the actual living-stock lot(s) supplying this Plot. Source total must equal the number received.
              </p>
              <div className="mt-3 space-y-2">
                {targetStock.map((row) => (
                  <div key={row.lotId} className="grid gap-3 rounded-xl border p-3 sm:grid-cols-[1.5fr_1fr_0.8fr] sm:items-center">
                    <div>
                      <p className="font-medium">{row.nurseryName} · {row.bedName}</p>
                      <p className="text-xs text-muted-foreground">{row.lotCode} · {number.format(row.readyQuantity)} ready</p>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {row.varietyDetailStatus.replace("_", " ").toLowerCase()} variety detail
                    </div>
                    <Input
                      type="number"
                      min="0"
                      max={row.readyQuantity}
                      placeholder="Quantity"
                      value={issuePanel.sourceQuantities[row.lotId] || ""}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setIssuePanel((current) => current ? ({
                          ...current,
                          sourceQuantities: { ...current.sourceQuantities, [row.lotId]: e.target.value },
                        }) : current)
                      }
                    />
                  </div>
                ))}
                {targetStock.length === 0 && (
                  <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                    No distribution-ready Nursery stock exists for this seedling catalogue item.
                  </div>
                )}
              </div>
              <p className={`mt-2 text-sm font-medium ${sourceTotal === Number(issuePanel.actualReceived) ? "text-primary" : "text-warning"}`}>
                Source total: {number.format(sourceTotal)} / {number.format(Number(issuePanel.actualReceived) || 0)}
              </p>
            </div>

            <div className="rounded-xl border bg-muted/20 p-4">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 size-4"
                  checked={issuePanel.recordVarieties}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setIssuePanel((current) => current ? ({ ...current, recordVarieties: e.target.checked }) : current)}
                />
                <span>
                  <span className="font-semibold">Record variety information now</span>
                  <span className="block text-sm text-muted-foreground">
                    Optional. Leave this off and issue can continue with the total seedlings only.
                  </span>
                </span>
              </label>

              {issuePanel.recordVarieties && (
                <div className="mt-4 space-y-4">
                  <div className="max-w-sm space-y-2">
                    <Label>Variety detail status</Label>
                    <Select
                      value={issuePanel.varietyStatus}
                      onValueChange={(value: VarietyDetailStatus) => setIssuePanel((current) => current ? ({ ...current, varietyStatus: value }) : current)}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PARTIAL">Partial · some variety information known</SelectItem>
                        <SelectItem value="COMPLETE">Complete · every seedling accounted for</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2 md:grid-cols-2">
                    {varieties.map((variety) => {
                      const checked = issuePanel.selectedVarieties.includes(variety.id);
                      return (
                        <div key={variety.id} className="rounded-xl border p-3">
                          <label className="flex cursor-pointer items-start gap-3">
                            <input
                              type="checkbox"
                              className="mt-1 size-4"
                              checked={checked}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => setIssuePanel((current) => {
                                if (!current) return current;
                                return {
                                  ...current,
                                  selectedVarieties: e.target.checked
                                    ? [...current.selectedVarieties, variety.id]
                                    : current.selectedVarieties.filter((id) => id !== variety.id),
                                };
                              })}
                            />
                            <span className="min-w-0 flex-1">
                              <span className="font-medium">{variety.varietyName}</span>
                              <span className="block text-xs text-muted-foreground">{variety.varietyCode} · {variety.coffeeType}</span>
                            </span>
                          </label>
                          {checked && (
                            <Input
                              className="mt-2"
                              type="number"
                              min="0"
                              placeholder="Quantity optional"
                              value={issuePanel.varietyQuantities[variety.id] || ""}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => setIssuePanel((current) => current ? ({
                                ...current,
                                varietyQuantities: { ...current.varietyQuantities, [variety.id]: e.target.value },
                              }) : current)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Known variety quantities: {number.format(knownVarietyTotal)} / {number.format(Number(issuePanel.actualReceived) || 0)}.
                    Partial detail may remain below the total.
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Distribution note</Label>
              <Textarea
                value={issuePanel.notes}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setIssuePanel((current) => current ? ({ ...current, notes: e.target.value }) : current)}
                placeholder="Optional note about receipt, shortages, condition or handover"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIssuePanel(undefined)}>Cancel</Button>
              <Button onClick={issue} disabled={working || targetStock.length === 0}>
                <PackageCheck className="mr-2 size-4" />Record actual issue
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {varietyPanel && (
        <Card className="border-primary/25" data-testid="seedling-variety-enrichment-panel">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Layers3 className="size-4 text-primary" />
              Variety detail · {varietyPanel.target.farmerName} · {varietyPanel.target.plotName}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This can be completed later. The original seedling receipt remains valid even when variety information is absent or partial.
            </p>
            <div className="max-w-sm space-y-2">
              <Label>Detail status</Label>
              <Select
                value={varietyPanel.status}
                onValueChange={(value: VarietyDetailStatus) => setVarietyPanel((current) => current ? ({ ...current, status: value }) : current)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NOT_RECORDED">Not recorded</SelectItem>
                  <SelectItem value="PARTIAL">Partial</SelectItem>
                  <SelectItem value="COMPLETE">Complete</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              {varieties.map((variety) => {
                const checked = varietyPanel.selectedVarieties.includes(variety.id);
                return (
                  <div key={variety.id} className="rounded-xl border p-3">
                    <label className="flex cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1 size-4"
                        checked={checked}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setVarietyPanel((current) => {
                          if (!current) return current;
                          return {
                            ...current,
                            selectedVarieties: e.target.checked
                              ? [...current.selectedVarieties, variety.id]
                              : current.selectedVarieties.filter((id) => id !== variety.id),
                          };
                        })}
                      />
                      <span>
                        <span className="font-medium">{variety.varietyName}</span>
                        <span className="block text-xs text-muted-foreground">{variety.varietyCode}</span>
                      </span>
                    </label>
                    {checked && (
                      <Input
                        className="mt-2"
                        type="number"
                        min="0"
                        placeholder="Quantity optional"
                        value={varietyPanel.quantities[variety.id] || ""}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setVarietyPanel((current) => current ? ({
                          ...current,
                          quantities: { ...current.quantities, [variety.id]: e.target.value },
                        }) : current)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setVarietyPanel(undefined)}>Cancel</Button>
              <Button onClick={saveVarieties} disabled={working}>Save variety detail</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Farmer Plot allocations</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Farmer / Farm / Plot</TableHead>
                  <TableHead>Seedlings</TableHead>
                  <TableHead>Variety detail</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plan.targets.map((target) => (
                  <TableRow key={target.id}>
                    <TableCell>
                      <p className="font-semibold">{target.farmerName || "Farmer"}</p>
                      <p className="text-xs text-muted-foreground">{target.farmName || "Farm"} · {target.plotName || "Plot"}</p>
                    </TableCell>
                    <TableCell>
                      <p className="font-semibold">{number.format(target.actualReceivedQuantity || 0)} received</p>
                      <p className="text-xs text-muted-foreground">{number.format(target.plannedQuantity)} planned</p>
                    </TableCell>
                    <TableCell>
                      <p className="capitalize">{target.varietyDetailStatus.replace("_", " ").toLowerCase()}</p>
                      {target.status === "ISSUED" && (
                        <Button variant="link" className="h-auto p-0 text-xs" onClick={() => openVariety(target)}>
                          Add / update varieties
                        </Button>
                      )}
                    </TableCell>
                    <TableCell><StatusBadge status={target.status} testId={`seedling-target-status-${target.id}`} /></TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        {["SANCTIONED", "IN_PROGRESS"].includes(plan.status) && ["PLANNED", "READY"].includes(target.status) && (
                          <Button size="sm" onClick={() => openIssue(target)}>
                            <PackageCheck className="mr-1 size-3.5" />Issue
                          </Button>
                        )}
                        {plan.status !== "COMPLETED" && target.status === "PLANNED" && (
                          <Button size="sm" variant="ghost" onClick={() => updateTargetStatus(target, "READY")}>
                            Ready
                          </Button>
                        )}
                        {plan.status !== "COMPLETED" && ["PLANNED", "READY"].includes(target.status) && (
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => updateTargetStatus(target, "MISSED")}>
                            <XCircle className="mr-1 size-3.5" />Missed
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {plan.targets.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">No Plot allocations in this Plan.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="text-sm text-muted-foreground">
        Need to inspect Nursery stock first? <Link to="/nursery" className="font-medium text-primary hover:underline">Open Nursery Management</Link>.
      </div>
    </div>
  );
}
